import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { useUserRole } from "./UserRoleContext";
import io from "socket.io-client";
import {
  notificationCountsForBadge,
  isActionRequiredType,
} from "../utils/notificationKinds";

const NotificationContext = createContext();
const GROUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

const SHOW_SYNC_NOTIFICATION_TYPES = new Set([
  "show_pending",
  "show_accepted",
  "show_declined",
  "show_cancelled",
  "show_updated",
  "show_booked",
]);

function dispatchShowsUpdated() {
  window.dispatchEvent(new CustomEvent("showsUpdated"));
}

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { userData } = useUserRole();
  const [notifications, setNotifications] = useState([]);
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deliveryPreferences, setDeliveryPreferences] = useState({});
  const deliveryPreferencesRef = useRef({});

  useEffect(() => {
    deliveryPreferencesRef.current = deliveryPreferences;
  }, [deliveryPreferences]);

  const unreadCount = useMemo(
    () => notificationCountsForBadge(notifications, userData?.role).badgeCount,
    [notifications, userData?.role],
  );

  const parseConversationId = (actionUrl) => {
    if (!actionUrl) return null;
    try {
      const url = new URL(actionUrl, window.location.origin);
      return url.searchParams.get("conversation");
    } catch {
      return null;
    }
  };

  const buildMessageGroupKey = (notif) => {
    if (!notif || notif.type !== "message_received") return null;

    const conversationId =
      notif.conversation_id ||
      parseConversationId(notif.action_url) ||
      (notif.metadata && notif.metadata.conversationId);

    if (conversationId) {
      return `conversation:${conversationId}`;
    }

    if (notif.sender_id) return `sender:${notif.sender_id}`;
    if (notif.sender_name) return `senderName:${notif.sender_name}`;
    return null;
  };

  const groupedNotifications = useMemo(() => {
    if (!notifications?.length) return [];

    const nonMessageNotifications = notifications.filter(
      (n) => n.type !== "message_received",
    );
    if (!nonMessageNotifications.length) return [];

    const sorted = [...nonMessageNotifications].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );

    const groups = [];

    sorted.forEach((notif) => {
      const createdAt =
        notif.created_at instanceof Date
          ? notif.created_at
          : new Date(notif.created_at);
      const groupKey = buildMessageGroupKey(notif);
      const isMessageGroup = Boolean(groupKey);

      if (!isMessageGroup) {
        groups.push({
          groupId: `single-${notif.notification_id}`,
          groupKey: null,
          count: 1,
          unreadCount: notif.is_read ? 0 : 1,
          latestCreatedAt: createdAt,
          latestNotification: notif,
          notifications: [notif],
          isMessageGroup: false,
          title: notif.title,
          message: notif.message,
        });
        return;
      }

      const existing = groups.find(
        (g) =>
          g.groupKey === groupKey &&
          Math.abs(g.latestCreatedAt - createdAt) <= GROUP_WINDOW_MS,
      );

      if (existing) {
        existing.notifications.push(notif);
        existing.count += 1;
        if (!notif.is_read) {
          existing.unreadCount += 1;
        }

        if (createdAt > existing.latestCreatedAt) {
          existing.latestCreatedAt = createdAt;
          existing.latestNotification = notif;
        }

        existing.title =
          existing.count > 1
            ? `${existing.count} new messages`
            : existing.latestNotification.title;
        existing.message =
          existing.count > 1
            ? existing.latestNotification.sender_name
              ? `From ${existing.latestNotification.sender_name}`
              : existing.latestNotification.message
            : existing.latestNotification.message;
        return;
      }

      groups.push({
        groupId: `${groupKey}-${notif.notification_id}`,
        groupKey,
        count: 1,
        unreadCount: notif.is_read ? 0 : 1,
        latestCreatedAt: createdAt,
        latestNotification: notif,
        notifications: [notif],
        isMessageGroup: true,
        title: notif.title,
        message: notif.message,
      });
    });

    return groups.sort(
      (a, b) => new Date(b.latestCreatedAt) - new Date(a.latestCreatedAt),
    );
  }, [notifications]);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!currentUser) return;

    try {
      const response = await fetch("/api/notifications", {
        credentials: "include",
      });
      const data = await response.json();

      if (data.success) {
        setNotifications(data.notifications);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchDeliveryPreferences = useCallback(async () => {
    if (!currentUser) return;
    try {
      const response = await fetch("/api/notifications/preferences", {
        credentials: "include",
      });
      const data = await response.json();
      if (data.success && data.preferences) {
        setDeliveryPreferences(data.preferences);
      }
    } catch (error) {
      console.error("Error fetching delivery preferences:", error);
    }
  }, [currentUser]);

  // Initialize socket connection and fetch notifications
  useEffect(() => {
    if (!currentUser || !userData) return;

    // Fetch initial notifications
    fetchNotifications();
    fetchDeliveryPreferences();

    // Connect to socket
    const newSocket = io("/", {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });

    newSocket.on("connect", () => {
      console.log("Socket connected:", newSocket.id);
    });

    const refetchAfterReconnect = () => {
      fetchNotifications();
      fetchDeliveryPreferences();
      dispatchShowsUpdated();
      window.dispatchEvent(new CustomEvent("messagingResync"));
    };

    newSocket.on("reconnect", refetchAfterReconnect);

    newSocket.on("shows_sync", () => {
      dispatchShowsUpdated();
      fetchNotifications();
    });

    newSocket.on("join_requests_sync", () => {
      window.dispatchEvent(new CustomEvent("joinRequestsUpdated"));
    });

    newSocket.on("artist_relationships_sync", (detail) => {
      window.dispatchEvent(
        new CustomEvent("artistRelationshipsUpdated", { detail: detail || {} }),
      );
    });

    // Listen for new notifications
    newSocket.on("new_notification", (notification) => {
      console.log("New notification received:", notification);
      const hydrated = {
        is_read: false,
        created_at: notification.created_at || new Date().toISOString(),
        ...notification,
      };
      setNotifications((prev) => [hydrated, ...prev]);

      // Optional: Show browser notification if permission granted (exclude message_received; messages have their own icon)
      const pushPreferenceKey = `${notification.type}_push`;
      const allowPush =
        deliveryPreferencesRef.current[pushPreferenceKey] !== false;
      if (
        notification.type !== "message_received" &&
        allowPush &&
        "Notification" in window &&
        Notification.permission === "granted"
      ) {
        new Notification(notification.title, {
          body: notification.message,
          icon: "/images/llmIcon.png",
        });
      }

      if (SHOW_SYNC_NOTIFICATION_TYPES.has(notification.type)) {
        dispatchShowsUpdated();
      }
    });

    newSocket.on("notification_count", () => {
      fetchNotifications();
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    setSocket(newSocket);

    // Cleanup
    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [
    currentUser,
    userData,
    fetchNotifications,
    fetchDeliveryPreferences,
  ]);

  // Mark notification as read
  const markAsRead = useCallback(
    async (notificationId) => {
      try {
        const response = await fetch(
          `/api/notifications/${notificationId}/read`,
          {
            method: "PATCH",
            credentials: "include",
          },
        );

        if (response.ok) {
          setNotifications((prev) =>
            prev.map((notif) =>
              notif.notification_id === notificationId
                ? { ...notif, is_read: true, read_at: new Date().toISOString() }
                : notif,
            ),
          );
        }
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    },
    [],
  );

  const markMultipleAsRead = useCallback(
    async (notificationIds = []) => {
      const idsToMark = notificationIds.filter(Boolean);
      if (!idsToMark.length) return;

      await Promise.all(
        idsToMark.map((id) =>
          fetch(`/api/notifications/${id}/read`, {
            method: "PATCH",
            credentials: "include",
          }),
        ),
      );

      setNotifications((prev) =>
        prev.map((notif) =>
          idsToMark.includes(notif.notification_id)
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif,
        ),
      );
    },
    [],
  );

  const markGroupAsRead = useCallback(
    async (group) => {
      if (!group?.notifications?.length) return;
      const unreadIds = group.notifications
        .filter((n) => !n.is_read)
        .map((n) => n.notification_id);

      if (!unreadIds.length) return;
      await markMultipleAsRead(unreadIds);
    },
    [markMultipleAsRead],
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await fetch("/api/notifications/read-all", {
        method: "PATCH",
        credentials: "include",
      });

      if (response.ok) {
        setNotifications((prev) =>
          prev.map((notif) =>
            isActionRequiredType(notif.type, userData?.role)
              ? notif
              : {
                  ...notif,
                  is_read: true,
                  read_at: new Date().toISOString(),
                },
          ),
        );
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  }, [userData?.role]);

  // Delete notification
  const deleteNotification = useCallback(
    async (notificationId) => {
      try {
        const response = await fetch(`/api/notifications/${notificationId}`, {
          method: "DELETE",
          credentials: "include",
        });

        if (response.ok) {
          setNotifications((prev) =>
            prev.filter(
              (notification) => notification.notification_id !== notificationId,
            ),
          );
        }
      } catch (error) {
        console.error("Error deleting notification:", error);
      }
    },
    [],
  );

  // Clear notifications by filter (all, unread, shows, activity)
  const clearFiltered = useCallback(
    async (filter) => {
      const validFilters = ["all", "unread", "shows", "activity"];
      if (!filter || !validFilters.includes(filter)) return;

      try {
        const response = await fetch("/api/notifications/clear-filtered", {
          method: "DELETE",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filter }),
        });

        const data = await response.json();
        if (response.ok && data.success) {
          const showTypesClearable = [
            "show_booked",
            "show_cancelled",
            "show_updated",
            "show_accepted",
            "show_declined",
          ];
          const activityTypesClearable = [
            "invite_accepted",
            "invite_declined",
            "join_request_approved",
            "join_request_rejected",
            "artist_requested",
            "artist_joined",
            "artist_left_bar",
            "admin_added",
            "admin_removed",
          ];
          const protectedTypes = new Set([
            "show_pending",
            "invite_received",
            "join_request_received",
          ]);

          setNotifications((prev) => {
            if (filter === "all") {
              return prev.filter((n) => protectedTypes.has(n.type));
            }
            if (filter === "unread") {
              return prev.filter((n) => n.is_read || protectedTypes.has(n.type));
            }
            if (filter === "shows") {
              return prev.filter((n) => !showTypesClearable.includes(n.type));
            }
            return prev.filter((n) => !activityTypesClearable.includes(n.type));
          });
        }
      } catch (error) {
        console.error("Error clearing filtered notifications:", error);
      }
    },
    [],
  );

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ("Notification" in window && Notification.permission === "default") {
      await Notification.requestPermission();
    }
  }, []);

  const value = {
    notifications,
    groupedNotifications,
    unreadCount,
    loading,
    socket,
    markAsRead,
    markGroupAsRead,
    markMultipleAsRead,
    markAllAsRead,
    deleteNotification,
    clearFiltered,
    fetchNotifications,
    requestNotificationPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
